import { useState, type ReactElement } from 'react';
import { apiFetch, ApiError } from '../../auth';
import { EmberDisc } from '../components/EmberDisc';
import { trackFailed } from '../../../../../shared-web/track';
import { PageBack } from '../components/PageBack';
import './Feedback.css';

/**
 * /feedback — the message board to the team (reached from the homepage footer,
 * the account menu and the What's new horn). Open to everyone, signed in or
 * not: the server binds the message to the session or the visitor cookie on
 * its own. Two required fields — the message and an email to write back to
 * (2026-09-14 拍板) — two optional ones the team can read it against, and one
 * yes/no: whether the person would sit down for a proper conversation with
 * the team (about 30 minutes, over a call).
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The fixed tenure choices; values are the server's vocabulary (feedback-routes.js). */
const USAGE_DURATIONS: Array<{ value: string; label: string }> = [
  { value: 'first_visit', label: 'Just discovered it' },
  { value: 'under_a_week', label: 'Less than a week' },
  { value: 'under_a_month', label: 'A few weeks' },
  { value: 'over_a_month', label: 'A month or more' },
];

export function FeedbackPage(): ReactElement {
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [wantsInterview, setWantsInterview] = useState(false);
  const [background, setBackground] = useState('');
  const [duration, setDuration] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (): Promise<void> => {
    const body = message.trim();
    const address = email.trim();
    if (!body || !EMAIL_RE.test(address) || sending) return;
    setSending(true);
    try {
      await apiFetch<{ ok: true; id: string }>('/api/feedback', {
        method: 'POST',
        body: {
          message: body,
          email: address,
          wants_interview: wantsInterview,
          ...(background.trim() ? { background: background.trim() } : {}),
          ...(duration ? { usage_duration: duration } : {}),
        },
      });
      setSent(true);
    } catch (err) {
      trackFailed('feedback_submit', err instanceof ApiError
        ? String((err.body as { error?: string })?.error || err.status)
        : 'network');
      setSending(false);
      return;
    }
    setSending(false);
  };

  return (
    <div className="ember-container feedback-page">
      <div className="feedback-toolbar">
        <PageBack className="feedback-back" />
      </div>

      {sent ? (
        <div className="feedback-done" role="status">
          <h1 className="feedback-title"><EmberDisc face="warm" className="feedback-disc" /><span>Thanks. We read every message.</span></h1>
          <p className="feedback-done-text">
            Your message is with the team &mdash; we read every one, and it genuinely
            shapes what we build.
            {wantsInterview
              ? ` And thank you for offering to talk with us: we’ll write to ${email.trim()} to find a time.`
              : ''}
          </p>
          <button
            type="button"
            className="ember-btn ember-btn-secondary"
            onClick={() => {
              setMessage('');
              setWantsInterview(false);
              setBackground('');
              setDuration('');
              setSent(false);
            }}
          >
            Send another message
          </button>
        </div>
      ) : (
        <>
          <header className="feedback-intro">
            <h1 className="feedback-title"><EmberDisc face="listening" className="feedback-disc" /><span>Message the team</span></h1>
            <p className="feedback-lede">
              Confused by something? Have an idea, a bug, or something you wish Ember did
              differently? This goes directly to the people building it, and every message
              is read.
            </p>
          </header>

          <form
            className="feedback-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <label className="feedback-field">
              <span className="feedback-label">What’s on your mind?</span>
              <textarea
                className="feedback-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us anything — what's working, what isn't, or what you need."
                rows={6}
                required
                dir="auto"
              />
            </label>

            <label className="feedback-field">
              <span className="feedback-label">Your email</span>
              <input
                className="feedback-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                inputMode="email"
                required
              />
              <span className="feedback-hint">So we can write back.</span>
            </label>

            <label className="feedback-check">
              <input
                type="checkbox"
                checked={wantsInterview}
                onChange={(e) => setWantsInterview(e.target.checked)}
              />
              <span>
                <strong>I’d be up for a conversation with the team.</strong>{' '}
                About 30 minutes, over a call, at a time that suits you. We regularly talk
                with people who use Ember to understand what’s working, what isn’t, and
                what you wish it could do. We’ll write to your email to set it up.
              </span>
            </label>

            <label className="feedback-field">
              <span className="feedback-label">
                About you <span className="feedback-optional">optional</span>
              </span>
              <textarea
                className="feedback-textarea feedback-textarea-short"
                value={background}
                onChange={(e) => setBackground(e.target.value)}
                placeholder="Anything about your situation that would help us understand?"
                rows={2}
                dir="auto"
              />
            </label>

            <label className="feedback-field">
              <span className="feedback-label">
                How long have you been using Ember? <span className="feedback-optional">optional</span>
              </span>
              <div className="feedback-select-wrap">
                <select
                  className="feedback-select"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  aria-label="How long have you used Ember?"
                >
                  <option value="">Select one</option>
                  {USAGE_DURATIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <svg className="feedback-select-chevron" viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M1 1.25 5 5.25 9 1.25" />
                </svg>
              </div>
            </label>

            <button
              type="submit"
              className="ember-btn ember-btn-primary feedback-send"
              disabled={!message.trim() || !EMAIL_RE.test(email.trim()) || sending}
            >
              {sending ? 'Sending…' : 'Send feedback'}
            </button>
          </form>

          <p className="feedback-email">
            Prefer email? Reach us anytime at{' '}
            <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>.
          </p>
        </>
      )}
    </div>
  );
}
